export enum NetworkType {
  SOLANA = 1,
  EVM = 0,
}

export enum WalletType {
  PRIMARY = 'primary',
  FUNDED = 'funded',
}
export enum ErrorCode {
  // Internal to FE
  UNKNOWN = 0,
  INTERNAL_ID_MISMATCH = 900,
  INTERNAL_CREATE_ORDER_TIMEOUT = 901,

  // General
  UNAUTHORIZED = 1002,
  TOO_MANY_REQUESTS = 1003,
  INVALID_SIGNATURE = 1022,
  INVALID_NONCE = 1023,
  ACCOUNT_NOT_FOUND = 1099,

  // Invalid request
  BAD_DECIMALS = 1111,
  INVALID_ORDER_TYPE = 1116,
  BAD_SYMBOL = 1121,
  INVALID_LISTEN_KEY = 1125,
  INVALID_PARAMETER = 1130,
  BAD_RECV_WINDOW = 1131,
  ORDER_EXPIRED = 1132,

  // Order rejection
  PRICE_CHECK_FAILED = 3001,
  MARGIN_CHECK_FAILED = 3002,
  PRICE_STALE = 3003,
  ORDER_STALE = 3004,
  PARTIAL_FILL = 3005,
  INCREASE_WITH_SL_TP = 3006,
  INCREASE_DURING_INSOLVENCY = 3007,
  DELETED_BY_LIQ = 3008,
  OI_CAP_EXCEEDED = 3010,
  INVALID_LIMIT_PRICE = 3011,
  PER_SYMBOL_PNL_EXCEEDED = 3013,
  DUPLICATE = 3015,
  ORDER_ACCOUNT_NOT_FOUND = 3016,
  ORDER_NOT_FOUND = 3017,
  REDUCE_ONLY_INCREASES = 3018,
  REDUCE_ONLY_EXCEEDS_SIZE = 3019,
  ORDER_CHANGES_LEVERAGE = 3020,
  SHORT_SELL_RESTRICTED = 3026,
  CANCEL_BY_SELF_TRADE = 3028,
  ALO_IMMEDIATE_FILL = 3029,
  FOK_PARTIAL_FILL = 3030,
  SLIPPAGE_WILL_FAIL_ACCOUNT = 3031,
  INVALID_TIME_IN_FORCE = 1133,
  INVALID_NOTIONAL_SIZE = 1152,
  REDUCE_ONLY_MARKET = 1153,

  // LP order rejection
  LP_INSUFFICIENT_BALANCE = 4001,
  LP_MARGIN_CHECK_FAILED = 4002,
  LP_PRICE_STALE = 4003,
  LP_ORDER_STALE = 4004,
  LP_PARTIAL_FILL = 4005,
  LP_ADD_DURING_SOLVENCY = 4006,
  LP_WITHDRAW_WITHOUT_SHARES = 4007,
  LP_REVERTED_ON_CHAIN = 4008,
  LP_WITHDRAW_CAP_EXCEEDED = 4009,
  LP_DUPLICATE = 4010,
  LP_ACCOUNT_NOT_FOUND = 4011,
  LP_WITHDRAW_EXCEEDS_BALANCE = 4012,
  LP_EXECUTION_FAILED = 4013,

  // Transfer rejection
  TRANSFER_MARGIN_CHECK_FAILED = 5002,
  TRANSFER_WITHDRAW_CAP_EXCEEDED = 5009,
  TRANSFER_DUPLICATE = 5010,
  TRANSFER_ACCOUNT_NOT_FOUND = 5011,

  // Referrals
  NOT_UNIQUE_REF_CODE = 1140,
  NOT_FOUND_REF_CODE = 1141,
  ALREADY_USE_REF_CODE = 1142,
  SOLANA_INVALID_TX_MEMO_NOT_FOUND = 1143,
  SOLANA_TX_IS_ALREADY_USED = 1144,
  PROMO_CODE_RESTRICTED_TO_ANOTHER_CUSTOMER = 4100,
  PROMO_CODE_FIRST_PURCHASE_ONLY = 4101,

  // Affiliates
  AFFILIATE_NOT_FOUND = 1300,

  EXECUTION_FAILED = 5013,

  // Health
  MAX_RETRIES_EXCEEDED = 9999,
}

export enum WsErrorCode {
  LISTEN_KEY_REQUIRED = 4003,
  LISTEN_KEY_NOT_FOUND = 4004,
  LISTEN_KEY_EXPIRED = 4005,

  // Health
  MAX_RETRIES_EXCEEDED = 9999,
}

// Helper function to get error messages with translations
export const getErrorMsg = (
  errorCode: ErrorCode,
  t: (key: string) => string,
): string => {
  const errorMsgMap: { [key in ErrorCode]: string } = {
    // Internal to FE
    [ErrorCode.UNKNOWN]: t('errorMsgs.unknown'),
    [ErrorCode.INTERNAL_ID_MISMATCH]: t('errorMsgs.internalIdMismatch'),
    [ErrorCode.INTERNAL_CREATE_ORDER_TIMEOUT]: t(
      'errorMsgs.internalCreateOrderTimeout',
    ),

    // General
    [ErrorCode.UNAUTHORIZED]: t('errorMsgs.unauthorized'),
    [ErrorCode.TOO_MANY_REQUESTS]: t('errorMsgs.tooManyRequests'),
    [ErrorCode.INVALID_SIGNATURE]: t('errorMsgs.invalidSignature'),
    [ErrorCode.INVALID_NONCE]: t('errorMsgs.invalidNonce'),
    [ErrorCode.ACCOUNT_NOT_FOUND]: t('errorMsgs.accountNotFound'),

    // Invalid request
    [ErrorCode.BAD_DECIMALS]: t('errorMsgs.badDecimals'),
    [ErrorCode.INVALID_ORDER_TYPE]: t('errorMsgs.invalidOrderType'),
    [ErrorCode.BAD_SYMBOL]: t('errorMsgs.badSymbol'),
    [ErrorCode.INVALID_LISTEN_KEY]: t('errorMsgs.invalidListenKey'),
    [ErrorCode.INVALID_PARAMETER]: t('errorMsgs.invalidParameter'),
    [ErrorCode.BAD_RECV_WINDOW]: t('errorMsgs.badRecvWindow'),
    [ErrorCode.ORDER_EXPIRED]: t('errorMsgs.orderExpired'),

    // Order rejection
    [ErrorCode.PRICE_CHECK_FAILED]: t('errorMsgs.priceCheckFailed'),
    [ErrorCode.MARGIN_CHECK_FAILED]: t('errorMsgs.marginCheckFailed'),
    [ErrorCode.PRICE_STALE]: t('errorMsgs.priceStale'),
    [ErrorCode.ORDER_STALE]: t('errorMsgs.orderStale'),
    [ErrorCode.PARTIAL_FILL]: t('errorMsgs.partialFill'),
    [ErrorCode.INCREASE_WITH_SL_TP]: t('errorMsgs.increaseWithSlTp'),
    [ErrorCode.INCREASE_DURING_INSOLVENCY]: t(
      'errorMsgs.increaseDuringInsolvency',
    ),
    [ErrorCode.DELETED_BY_LIQ]: t('errorMsgs.deletedByLiq'),
    [ErrorCode.OI_CAP_EXCEEDED]: t('errorMsgs.oiCapExceeded'),
    [ErrorCode.INVALID_LIMIT_PRICE]: t('errorMsgs.invalidLimitPrice'),
    [ErrorCode.DUPLICATE]: t('errorMsgs.duplicate'),
    [ErrorCode.ORDER_NOT_FOUND]: t('errorMsgs.orderNotFound'),
    [ErrorCode.REDUCE_ONLY_INCREASES]: t('errorMsgs.reduceOnlyIncreases'),
    [ErrorCode.REDUCE_ONLY_EXCEEDS_SIZE]: t('errorMsgs.reduceOnlyExceedsSize'),
    [ErrorCode.ORDER_CHANGES_LEVERAGE]: t('errorMsgs.orderChangesLeverage'),
    [ErrorCode.ORDER_ACCOUNT_NOT_FOUND]: t('errorMsgs.orderAccountNotFound'),
    [ErrorCode.PER_SYMBOL_PNL_EXCEEDED]: t('errorMsgs.perSymbolPnlExceeded'),
    [ErrorCode.SHORT_SELL_RESTRICTED]: t('errorMsgs.shortSellRestricted'),
    [ErrorCode.CANCEL_BY_SELF_TRADE]: t('errorMsgs.cancelBySelfTrade'),
    [ErrorCode.ALO_IMMEDIATE_FILL]: t('errorMsgs.aloImmediateFill'),
    [ErrorCode.FOK_PARTIAL_FILL]: t('errorMsgs.fokPartialFill'),
    [ErrorCode.SLIPPAGE_WILL_FAIL_ACCOUNT]: t(
      'errorMsgs.slippageWillFailAccount',
    ),
    [ErrorCode.INVALID_TIME_IN_FORCE]: t('errorMsgs.invalidTimeInForce'),
    [ErrorCode.INVALID_NOTIONAL_SIZE]: t('errorMsgs.invalidNotionalSize'),
    [ErrorCode.REDUCE_ONLY_MARKET]: t('errorMsgs.reduceOnlyMarket'),

    // LP order rejection
    [ErrorCode.LP_INSUFFICIENT_BALANCE]: t('errorMsgs.lpInsufficientBalance'),
    [ErrorCode.LP_WITHDRAW_WITHOUT_SHARES]: t(
      'errorMsgs.lpWithdrawWithoutShares',
    ),
    [ErrorCode.LP_WITHDRAW_EXCEEDS_BALANCE]: t(
      'errorMsgs.lpWithdrawExceedsBalance',
    ),
    [ErrorCode.LP_MARGIN_CHECK_FAILED]: t('errorMsgs.lpMarginCheckFailed'),
    [ErrorCode.LP_PRICE_STALE]: t('errorMsgs.lpPriceStale'),
    [ErrorCode.LP_ORDER_STALE]: t('errorMsgs.lpOrderStale'),
    [ErrorCode.LP_PARTIAL_FILL]: t('errorMsgs.lpPartialFill'),
    [ErrorCode.LP_ADD_DURING_SOLVENCY]: t('errorMsgs.lpAddDuringSolvency'),
    [ErrorCode.LP_REVERTED_ON_CHAIN]: t('errorMsgs.lpRevertedOnChain'),
    [ErrorCode.LP_WITHDRAW_CAP_EXCEEDED]: t('errorMsgs.lpWithdrawCapExceeded'),
    [ErrorCode.LP_DUPLICATE]: t('errorMsgs.lpDuplicate'),
    [ErrorCode.LP_ACCOUNT_NOT_FOUND]: t('errorMsgs.lpAccountNotFound'),
    [ErrorCode.LP_EXECUTION_FAILED]: t('errorMsgs.lpExecutionFailed'),

    // Transfer rejection
    [ErrorCode.TRANSFER_WITHDRAW_CAP_EXCEEDED]: t(
      'errorMsgs.transferWithdrawCapExceeded',
    ),
    [ErrorCode.TRANSFER_MARGIN_CHECK_FAILED]: t(
      'errorMsgs.transferMarginCheckFailed',
    ),
    [ErrorCode.TRANSFER_DUPLICATE]: t('errorMsgs.transferDuplicate'),
    [ErrorCode.TRANSFER_ACCOUNT_NOT_FOUND]: t(
      'errorMsgs.transferAccountNotFound',
    ),

    // Referrals
    [ErrorCode.NOT_UNIQUE_REF_CODE]: t('errorMsgs.notUniqueRefCode'),
    [ErrorCode.NOT_FOUND_REF_CODE]: t('errorMsgs.notFoundRefCode'),
    [ErrorCode.ALREADY_USE_REF_CODE]: t('errorMsgs.alreadyUseRefCode'),
    [ErrorCode.MAX_RETRIES_EXCEEDED]: t('errorMsgs.maxRetriesExceeded'),
    [ErrorCode.SOLANA_INVALID_TX_MEMO_NOT_FOUND]: t(
      'errorMsgs.solanaInvalidTxMemoNotFound',
    ),
    [ErrorCode.SOLANA_TX_IS_ALREADY_USED]: t('errorMsgs.solanaTxIsAlreadyUsed'),
    [ErrorCode.PROMO_CODE_RESTRICTED_TO_ANOTHER_CUSTOMER]: t(
      'errorMsgs.promoCodeRestrictedToAnotherCustomer',
    ),
    [ErrorCode.PROMO_CODE_FIRST_PURCHASE_ONLY]: t(
      'errorMsgs.promoCodeFirstPurchaseOnly',
    ),
    [ErrorCode.AFFILIATE_NOT_FOUND]: t('errorMsgs.affiliateNotFound'),
    [ErrorCode.EXECUTION_FAILED]: t('errorMsgs.executionFailed'),
  };

  return errorMsgMap[errorCode];
};

// Legacy ErrorMsgs object for backward compatibility (English only)
// Use getErrorMsg() function for translated error messages
export const ErrorMsgs: { [key in ErrorCode]: string } = {
  // Internal to FE
  [ErrorCode.UNKNOWN]:
    'Something went wrong. Please try again or contact support.',
  [ErrorCode.INTERNAL_ID_MISMATCH]:
    'Something went wrong. Please try again or contact support.',
  [ErrorCode.INTERNAL_CREATE_ORDER_TIMEOUT]:
    'Request timed out. Please try again.',

  // General
  [ErrorCode.UNAUTHORIZED]: "You're not authorized to perform this action.",
  [ErrorCode.TOO_MANY_REQUESTS]:
    'Too many requests. Please wait a moment and try again.',
  [ErrorCode.INVALID_SIGNATURE]:
    'Your session has expired. Please reconnect your wallet.',
  [ErrorCode.INVALID_NONCE]:
    'Your session has expired. Please reconnect your wallet.',
  [ErrorCode.ACCOUNT_NOT_FOUND]:
    'Account not found. Please reconnect your wallet.',

  // Invalid request
  [ErrorCode.BAD_DECIMALS]: 'Too many decimal places. Please round your value.',
  [ErrorCode.INVALID_ORDER_TYPE]:
    'Invalid order type. Please select a valid order type.',
  [ErrorCode.BAD_SYMBOL]: 'Invalid market. Please select a valid market.',
  [ErrorCode.INVALID_LISTEN_KEY]:
    'Connection expired. Please refresh the page.',
  [ErrorCode.INVALID_PARAMETER]:
    'Invalid input. Please check your values and try again.',
  [ErrorCode.BAD_RECV_WINDOW]: 'Request expired. Please try again.',
  [ErrorCode.ORDER_EXPIRED]:
    'Order expired, possibly due to a slow connection. Please try again.',

  // Order rejection
  [ErrorCode.PRICE_CHECK_FAILED]:
    'Order rejected — the execution price moved past your limit. Try increasing your slippage tolerance or using a limit order.',
  [ErrorCode.MARGIN_CHECK_FAILED]:
    'Insufficient balance. Please deposit more funds or reduce your order size.',
  [ErrorCode.PRICE_STALE]:
    'Price data is stale. Please wait a moment and try again.',
  [ErrorCode.ORDER_STALE]:
    'Order expired before it could be processed. Please try again.',
  [ErrorCode.PARTIAL_FILL]: 'Your order was only partially filled.',
  [ErrorCode.INCREASE_WITH_SL_TP]:
    'Cannot add to this position while TP/SL orders are active. Remove your TP/SL first, then try again.',
  [ErrorCode.INCREASE_DURING_INSOLVENCY]:
    'Trading is temporarily restricted. You can only reduce or close positions.',
  [ErrorCode.DELETED_BY_LIQ]:
    'Your order was canceled because your position was liquidated.',
  [ErrorCode.OI_CAP_EXCEEDED]:
    'This market has reached its position limit. Try a smaller size.',
  [ErrorCode.INVALID_LIMIT_PRICE]:
    'Your limit price is invalid. Please adjust and try again.',
  [ErrorCode.DUPLICATE]:
    'This order was already submitted. Check your open orders.',
  [ErrorCode.ORDER_NOT_FOUND]:
    'Order not found. It may have already been filled or canceled.',
  [ErrorCode.REDUCE_ONLY_INCREASES]:
    'This reduce-only order would open or increase a position. Please adjust your order.',
  [ErrorCode.REDUCE_ONLY_EXCEEDS_SIZE]:
    'Your reduce-only orders exceed your current position size. Cancel some existing orders or reduce the size.',
  [ErrorCode.ORDER_CHANGES_LEVERAGE]:
    'This order would change your leverage. Adjust your leverage first, then place the order.',
  [ErrorCode.ORDER_ACCOUNT_NOT_FOUND]:
    'Account not found. Please reconnect your wallet.',
  [ErrorCode.PER_SYMBOL_PNL_EXCEEDED]:
    'This market is in reduce-only mode. You can only close or decrease your position.',
  [ErrorCode.SHORT_SELL_RESTRICTED]:
    'This market has reached its position limit. Try a smaller size.',
  [ErrorCode.SLIPPAGE_WILL_FAIL_ACCOUNT]:
    "This trade's potential slippage could breach your margin requirements and risk liquidation. Please reduce your order size.",
  [ErrorCode.INVALID_TIME_IN_FORCE]:
    'Invalid time-in-force. Please select GTC, FOK, IOC, or Post Only.',
  [ErrorCode.INVALID_NOTIONAL_SIZE]:
    'Order value is too small or too large. Please adjust your order size.',
  [ErrorCode.REDUCE_ONLY_MARKET]:
    'This market is in reduce-only mode. You can only close or decrease your position.',

  [ErrorCode.CANCEL_BY_SELF_TRADE]:
    'Order canceled — it would have matched with another of your own orders.',
  [ErrorCode.ALO_IMMEDIATE_FILL]:
    'Post-only order rejected — it would have filled immediately. Adjust your price or use a different order type.',
  [ErrorCode.FOK_PARTIAL_FILL]:
    'Fill-or-kill order rejected — not enough liquidity to fill your entire order.',

  // LP order rejection
  [ErrorCode.LP_INSUFFICIENT_BALANCE]:
    'Insufficient balance to provide liquidity. Please deposit more funds.',
  [ErrorCode.LP_WITHDRAW_WITHOUT_SHARES]: 'You have no liquidity to withdraw.',
  [ErrorCode.LP_WITHDRAW_EXCEEDS_BALANCE]:
    'Withdrawal amount exceeds your liquidity balance. Please enter a smaller amount.',
  [ErrorCode.LP_MARGIN_CHECK_FAILED]:
    'Insufficient balance. Please deposit more funds.',
  [ErrorCode.LP_PRICE_STALE]:
    'Price data is stale. Please wait a moment and try again.',
  [ErrorCode.LP_ORDER_STALE]: 'Request expired. Please try again.',
  [ErrorCode.LP_PARTIAL_FILL]:
    'Your liquidity order was only partially filled.',
  [ErrorCode.LP_ADD_DURING_SOLVENCY]:
    'Liquidity deposits are temporarily restricted.',
  [ErrorCode.LP_REVERTED_ON_CHAIN]:
    'Transaction failed. Please try again or contact support.',
  [ErrorCode.LP_WITHDRAW_CAP_EXCEEDED]:
    'Withdrawal limit reached. Please try a smaller amount or try again later.',
  [ErrorCode.LP_DUPLICATE]:
    'This request was already submitted. Check your recent activity.',
  [ErrorCode.LP_ACCOUNT_NOT_FOUND]:
    'Account not found. Please reconnect your wallet.',
  [ErrorCode.LP_EXECUTION_FAILED]:
    'Transaction failed. Please try again or contact support.',

  // Transfer rejection
  [ErrorCode.TRANSFER_WITHDRAW_CAP_EXCEEDED]:
    'Withdrawal limit reached. Please try a smaller amount or try again later.',
  [ErrorCode.TRANSFER_MARGIN_CHECK_FAILED]:
    'Insufficient balance for this withdrawal.',
  [ErrorCode.TRANSFER_DUPLICATE]:
    'This withdrawal was already submitted. Check your recent activity.',
  [ErrorCode.TRANSFER_ACCOUNT_NOT_FOUND]:
    'Account not found. Please reconnect your wallet.',

  // Referrals
  [ErrorCode.NOT_UNIQUE_REF_CODE]:
    'This referral code has already been claimed.',
  [ErrorCode.NOT_FOUND_REF_CODE]:
    'Referral code not found. Please check and try again.',
  [ErrorCode.ALREADY_USE_REF_CODE]: "You've already used a referral code.",
  [ErrorCode.MAX_RETRIES_EXCEEDED]: 'Connection lost. Please refresh the page.',
  [ErrorCode.SOLANA_INVALID_TX_MEMO_NOT_FOUND]:
    'Transaction could not be verified. Please check your transaction and try again.',
  [ErrorCode.PROMO_CODE_RESTRICTED_TO_ANOTHER_CUSTOMER]:
    'This promo code is restricted to another customer.',
  [ErrorCode.PROMO_CODE_FIRST_PURCHASE_ONLY]:
    'This promo code is available for first purchase only.',
  [ErrorCode.AFFILIATE_NOT_FOUND]: 'Affiliate not found.',
  [ErrorCode.SOLANA_TX_IS_ALREADY_USED]:
    'This transaction has already been processed.',
  [ErrorCode.EXECUTION_FAILED]:
    'Something went wrong. Please try again or contact support.',
} as const;

export enum OrderStatus {
  NEW = 'NEW',
  PARTIALLY_FILLED = 'PARTIALLY_FILLED',
  FILLED = 'FILLED',
  CANCELLED = 'CANCELLED',
  REJECTED = 'REJECTED',
  VERIFIED = 'VERIFIED',
  PROCESSING = 'PROCESSING',
}

export enum OrderStatusV2 {
  PENDING = 0,
  FILLED = 1,
  CANCELLED = 2,
  REJECTED = 3,
  PARTIALLY_FILLED = 4,
}

export enum OrderType {
  MARKET = 'MARKET',
  LIMIT = 'LIMIT',
  STOP_LOSS = 'STOP_LOSS',
  TAKE_PROFIT = 'TAKE_PROFIT',
  LIQUIDATION = 'LIQUIDATION', // only in response
  SCALE = 'SCALE',
}

export enum OrderTypeV2 {
  MARKET = 0,
  LIMIT = 1,
  TAKE_PROFIT = 2,
  STOP_LOSS = 3,
  LIQUIDATION = 4,
  MARKET_STOP_LOSS = 5,
  MARKET_TAKE_PROFIT = 6,
}

export enum PositionSide {
  LONG = 0,
  SHORT = 1,
}

export enum TransferType {
  DEPOSIT = 'DEPOSIT',
  WITHDRAW = 'WITHDRAW',
  FUNDED_DEPOSIT = 'INTERNAL DEPOSIT',
  FUNDED_PROFIT_WITHDRAW = 'WITHDRAW PROFIT',
  CREDIT = 'CREDIT', // same logic as for FUNDED_DEPOSIT
}

export enum LPOrderType {
  DEPOSIT = 'DEPOSIT',
  IMMEDIATE_WITHDRAW = 'IMMEDIATE_WITHDRAW',
  SCHEDULE_WITHDRAW = 'SCHEDULE_WITHDRAW',
}

export enum OrderHistoryFilterType {
  ALL = 'all',
  FILLED = 'filled',
  REJECTED = 'rejected',
  CANCELLED = 'cancelled',
}

export enum TableTabType {
  POSITIONS = 'positions',
  OPEN_ORDERS = 'open-orders',
  ORDER_HISTORY = 'order-history',
  BALANCES = 'balances',
  TRANSFERS = 'transfers',
  LOCKED_ASSET_COLLATERAL = 'locked-asset-collateral',
}

export enum AssetType {
  STOCK = 'stock',
  FOREX = 'forex',
  CRYPTO = 'crypto',
}

export enum WsType {
  PUBLIC,
  PRIVATE,
}

export enum CapitalStatus {
  CREATED = 'created',
  PENDING_PAYMENT = 'pending_payment',
  ACTIVE = 'active',
  FUNDED = 'funded_active',
  PRE_FUNDED = 'pre_funded', // FE only: wallet was created for funding, but registration failed
  PRE_EVAL = 'pre_eval', // FE only: wallet was created for eval, but registration failed
  FUNDED_CLAIMED = 'claimed',
  GOAL_PASSED = 'goal_passed',
  GOAL_FAILED_MAX_DRAWDOWN = 'goal_failed_max_drawdown',
  GOAL_FAILED_MAX_DAILY_LOSS = 'goal_failed_max_daily_loss',
  FUNDED_FAILED_MAX_DRAWDOWN = 'funded_failed_max_drawdown',
  FUNDED_FAILED_MAX_DAILY_LOSS = 'funded_failed_max_daily_loss',
  PAID = 'paid',
  UNKNOWN = 'unknown',
  NOT_ELIGIBLE = 'not_eligible',
  BLOCKED = 'blocked',
  FUNDED_BLOCKED = 'funded_blocked',
}

export enum PrivyStatus {
  UP = 'UP',
  HAS_ISSUES = 'HASISSUES',
  UNDER_MAINTENANCE = 'UNDERMAINTENANCE',
}

export enum MarketStatus {
  TRADING = 'TRADING',
  REDUCE_ONLY = 'REDUCE_ONLY',
  HALT = 'HALT',
}

export enum TimeInForce {
  GTC = 'GTC',
  IOC = 'IOC',
  FOK = 'FOK',
  ALO = 'ALO',
}

export enum TimeInForceV2 {
  GTC = 0,
  DAY = 1,
  GTE = 2,
  IOC = 3,
  FOK = 4,
}

export enum ActivityTab {
  DEPTH = 'depth',
  TRADES = 'trades',
}

export enum SettingsTab {
  TRADING = 'trading',
  CHART = 'chart',
  NOTIFICATIONS = 'notifications',
}

export enum NotificationChannelType {
  EMAIL = 'email',
}

export enum NotificationChannelStatus {
  ENABLED = 'enabled',
  UNVERIFIED = 'unverified',
}

export enum DiscountStatus {
  ACTIVE = 'ACTIVE',
  USED = 'USED',
  EXPIRED = 'EXPIRED',
  REVOKED = 'REVOKED',
}

export enum EventType {
  AUTH_FLOW_STARTED = 'auth_flow_started', // after "LOG IN" click
  ACCOUNT_REGISTERED = 'account_registered', // after /register
  REGISTRATION_FAILED = 'registration_failed',
  DEPOSIT_COMPLETED = 'deposit_completed',
  WITHDRAW_INITIATED = 'withdraw_initiated',
  ORDER_PLACED = 'order_placed',
  ORDER_PLACED_SCALE = 'order_placed_scale',
  ORDER_REJECTED = 'order_rejected',
  OI_CAP_EXCEEDED = 'oi_cap_exceeded',
  APP_LAUNCHED = 'app_launched',
  MARKET_SEARCHED = 'market_searched',
  MARKET_REQUESTED = 'market_requested',
  LP_VIEWED = 'lp_viewed',
  CHECKOUT_STARTED = 'checkout_started', // Vest Capital checkout
  CHECKOUT_COMPLETED = 'checkout_completed',
  SLIPPAGE_EXCEEDED = 'slippage_exceeded',
  FUNDED_COMPENSATION_REQUESTED = 'funded_compensation_requested',
  UNLISTED_MARKET_VIEWED = 'unlisted_market_viewed',
  UNLISTED_MARKET_REQUESTED = 'unlisted_market_requested',
  PROMO_CODE_USED = 'promo_code_used',
  LOG_OUT = 'log_out',
  LOG_IN = 'log_in',
  EXCEPTION_CAUGHT = 'exception_caught',
  REQUEST_FAILED = 'request_failed',
}

export enum PnlType {
  NET, // includes fees, funding and slippage (using estFillPrice)
  MARKET, // excludes fees, funding and slippage (using markPrice)
  NET_MARKET, // includes fees and funding but excludes slippage (using markPrice)
}

export enum PositionEventType {
  UNSPECIFIED = 'POSITION_EVENT_TYPE_UNSPECIFIED',
  OPENED = 'POSITION_EVENT_TYPE_OPENED',
  CLOSED = 'POSITION_EVENT_TYPE_CLOSED',
  APPENDED = 'POSITION_EVENT_TYPE_APPENDED',
  REDUCED = 'POSITION_EVENT_TYPE_REDUCED',
  UPDATED = 'POSITION_EVENT_TYPE_UPDATED',
}

export enum TransactionType {
  DEPOSIT = 0,
  WITHDRAW = 1,
}

export enum AccountType {
  DEFAULT = 0,
  FUNDED = 1,
}

export enum AppAction {
  LOG_IN = 'log_in',
}
